import React, { useState } from "react";
import useCreate from "../../hooks/useCreate";
import useFetch from "../../hooks/useFetch";
import useForm from "../../hooks/useForm";
import { validateProduct } from "../../helpers/productValidation";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Save, ArrowLeft } from "lucide-react";

function CreateProduct() {
  const navigate = useNavigate();
  const { create, loading } = useCreate("/products");
  const [submitMode, setSubmitMode] = useState("create");

  const { data: categories = [] } = useFetch("/categories");

  const {
    formData,
    errors,
    handleChange,
    handleSubmit,
    resetForm,
    setFormData,
  } = useForm({
    initialValues: {
      name: "",
      description: "",
      useful_life: "",
      risk: "",
      minimum_stock: "",
      cum: "",
      category_id: "",
    },
    validate: validateProduct,
    loading,
    onSubmit: async (data) => {
      await create(data);
      toast.success("Producto creado exitosamente");

      if (submitMode === "createAndContinue") {
        resetForm();
      } else {
        navigate("/products");
      }
    },
  });

  const handleCategoryChange = (event) => {
    handleChange(event);
    if (event.target.value !== "11") {
      setFormData((previous) => ({ ...previous, cum: "" }));
    }
  };

  const showCum = String(formData.category_id) === "11";

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-xl mx-auto">
      <button
        onClick={() => navigate("/products")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver a productos
      </button>

      <h2 className="text-3xl font-semibold text-gray-800 mb-6">
        Crear Producto
      </h2>

      {errors.general && (
        <p className="text-red-600 text-sm mb-3">{errors.general}</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Categoría */}
        <div>
          <label className="block text-sm font-medium mb-1">Categoría</label>
          <select
            name="category_id"
            value={formData.category_id}
            onChange={handleCategoryChange}
            className={`w-full border px-4 py-2 rounded-lg ${
              errors.category_id ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Seleccionar categoría</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          {errors.category_id && (
            <p className="text-red-600 text-sm">{errors.category_id}</p>
          )}
        </div>


        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium mb-1">Nombre</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full border px-4 py-2 rounded-lg ${
              errors.name ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.name && <p className="text-red-600 text-sm">{errors.name}</p>}
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium mb-1">Descripción</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="w-full border px-4 py-2 rounded-lg h-28 resize-none"
          />
        </div>

         <div>
          <label className="block text-sm font-medium mb-1">Stock mínimo</label>
          <input
            type="number"
            min="0"
            step="1"
            name="minimum_stock"
            value={formData.minimum_stock}
            onChange={handleChange}
            className="w-full border border-gray-300 px-4 py-2 rounded-lg"
          />
        </div>

        {/* Vida útil y riesgo */}
        <div
          className={`grid gap-4 ${showCum ? "grid-cols-3" : "grid-cols-2"}`}
        >
          <div>
            <label className="block text-sm font-medium mb-1">Vida útil</label>
            <input
              name="useful_life"
              value={formData.useful_life}
              onChange={handleChange}
              className={`w-full border px-4 py-2 rounded-lg ${
                errors.useful_life ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.useful_life && (
              <p className="text-red-600 text-sm">{errors.useful_life}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Riesgo</label>
            <input
              name="risk"
              value={formData.risk}
              onChange={handleChange}
              className={`w-full border px-4 py-2 rounded-lg ${
                errors.risk ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.risk && (
              <p className="text-red-600 text-sm">{errors.risk}</p>
            )}
          </div>

          {showCum && (
            <div>
              <label className="block text-sm font-medium mb-1">CUM</label>
              <input
                type="text"
                name="cum"
                value={formData.cum}
                onChange={handleChange}
                className="w-full border border-gray-300 px-4 py-2 rounded-lg"
              />
            </div>
          )}
        </div>

        {/* Botones */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            onClick={() => setSubmitMode("create")}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg shadow ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white"
            }`}
          >
            <Save size={18} />
            Crear
          </button>

          <button
            type="submit"
            disabled={loading}
            onClick={() => setSubmitMode("createAndContinue")}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg shadow ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            <Save size={18} />
            Crear y seguir
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateProduct;
